import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { SortingState } from "@tanstack/react-table";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import LoaderErrorWrapper from "Common/Components/LoaderErrorWrapper/LoaderErrorWrapper";
import {
  fetchAllServiceQuotations,
  handleServiceQuotationStatus,
  deleteServiceQuotation,
  downloadServiceQuotationPdf,
} from "slices/quotation";
import {
  downloadCalibrationEngineerPdf,
  downloadRequisitionPdf,
} from "slices/sales_order/thunk";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import QuotationLogStatusDropdown from "Common/Components/StatusDropdown/quotationLogStatusDropdown";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UndoIcon from "@mui/icons-material/Undo";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import { Col, Form, Modal, Row } from "react-bootstrap";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import { toast } from "react-toastify";
import FollowUpPopUp from "components/FollowUpModal/FollowUpPopUp";
import FollowupModel from "components/FollowUpModal/FollowUpModel";
import { QUOTATION_STAGE, QUOTATION_TYPE } from "Constants/StatusConstant";
import { showConfirm, showError, showSuccess, showLoading, hideLoading } from "helpers/swalHelper";
import { Tooltip } from "@mui/material";
import DOMPurify from "dompurify";
import { exportServiceQuotationCSV } from "slices/quotation/serviceThunk";
import { app_modules } from "helpers/apps_modules_list";
import usePermission from "hooks/usePermission";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import { formatIndianAmount } from "helpers/formatIndianAmount";
import Chip from "components/Chips/Chip";
import FormActionButtons from "pages/QuotationV2/Form/Components/FormActionButtons";

const QuotationLog: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const tableState = usePreservedTableState({
    key: TableKeys.QUOTATION.SERVICE.LOG,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  // State management
  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  // const [search, setSearch] = useState("");
  // const [searchInput, setSearchInput] = useState("");
  // const [columnFilters, setColumnFilters] = useState<any>();
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  const [tableInstance, setTableInstance] = useState<any>();
  const [data, setData] = useState<any[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  // Follow Up Modal  states
  const [showFollowUpModal, setShowFollowUpModal] = useState(false);
  const [selectedFollowUpItem, setSelectedFollowUpItem] = useState<any>(null);

  // Download PDF modal (signature toggle)
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfQuotationId, setPdfQuotationId] = useState<string | null>(null);
  const [pdfQuotationNumber, setPdfQuotationNumber] = useState("");
  const [withSignature, setWithSignature] = useState(true);
  const [pdfDownloading, setPdfDownloading] = useState(false);

  // Redux selectors
  const { loading, error } = useSelector((state: any) => state.Quotation);

  const { canView, canUpdate } = usePermission(
    app_modules?.quotation?.app_name,
    app_modules?.quotation?.modules?.service
  );

  // Sorting parameters
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  // Effects
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchInput]);

  // Data fetching
  const handleFetchQuotationData = useCallback(async () => {
    // Debug: Log filter value being sent
    console.log("QuotationLog - Sending filters:", filterValue);
    try {
      const resData = await dispatch(
        fetchAllServiceQuotations({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortField ? sortDirection : "",
          search: search,
          filters: filterValue,
          type: "Log",
        })
      ).unwrap();
      setData(resData?.data || []);
      setTotalRecords(resData?.rowCount || 0);
    } catch (err: any) {
      console.error("Error fetching quotations:", err);
      toast.error(err?.message || "Failed to fetch quotations");
      setData([]);
      setTotalRecords(0);
    }
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sortField,
    sortDirection,
    filterValue,
    search,
  ]);

  // Effects
  useEffect(() => {
    handleFetchQuotationData();
  }, [handleFetchQuotationData]);

  // Handlers
  const viewRoute = "/quotation/service/view";

  const handleView = (quotationNo: string) => {
    navigate(`${viewRoute}/${quotationNo}`);
  };

  const handleEdit = (quotationNo: string) => {
    navigate(`/quotation/service/edit/${quotationNo}`);
  };

  const handleFollowUp = (quotationNo: string) => {
    navigate(`/quotation/followups/service/${quotationNo}`);
  };

  const handleCreateSalesOrder = (quotationNo: string) => {
    navigate(`/sales_order/service/create-from-quotation/${quotationNo}`);
  };

  const handleDelete = async (id: string, enquiryNumber?: string) => {
    const isFromEnquiry = Boolean(enquiryNumber);
    const result = await showConfirm(
      isFromEnquiry
        ? "This quotation will be reverted and the enquiry status will be changed back to 'In Progress'. Do you want to continue?"
        : "This quotation will be permanently deleted. Do you want to continue?",
      isFromEnquiry ? "Revert Quotation" : "Delete Quotation",
      isFromEnquiry ? "Yes, revert" : "Yes, delete",
      "Cancel"
    );

    if (!result.isConfirmed) return;

    try {
      await dispatch(deleteServiceQuotation(id)).unwrap();
      toast.success(
        isFromEnquiry
          ? "Quotation reverted successfully"
          : "Quotation deleted successfully"
      );
      handleFetchQuotationData();
    } catch (err: any) {
      console.error("Error deleting quotation:", err);
      toast.error(err?.message || "Failed to delete quotation");
    }
  };

  const openPdfModal = (id: string, quotationNumber: string) => {
    setPdfQuotationId(id);
    setPdfQuotationNumber(quotationNumber);
    setWithSignature(true);
    setShowPdfModal(true);
  };

  const closePdfModal = () => {
    setShowPdfModal(false);
    setPdfQuotationId(null);
    setPdfQuotationNumber("");
    setWithSignature(true);
  };

  const handleDownloadPdf = async () => {
    if (!pdfQuotationId) return;
    setPdfDownloading(true);
    try {
      showLoading("Downloading PDF...");
      await dispatch(
        downloadServiceQuotationPdf({
          id: pdfQuotationId,
          quotationNumber: pdfQuotationNumber,
          withSignature,
        })
      ).unwrap();
      hideLoading();
      showSuccess("PDF downloaded successfully");
      closePdfModal();
    } catch (err: any) {
      hideLoading();
      console.error("Error downloading PDF:", err);
      showError(err?.message || "Failed to download PDF");
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleDownloadCalibrationEngineerPdf = async (
    salesOrderServiceId: string,
    salesOrderNumber: string,
  ) => {
    try {
      showLoading("Downloading PDF...");
      const res = await dispatch(
        downloadCalibrationEngineerPdf({
          salesOrderServiceId,
          salesOrderNumber,
        }),
      ).unwrap();

      hideLoading();
      if (res) {
        showSuccess("GRN Tech Form PDF downloaded successfully");
      }
    } catch (err: any) {
      hideLoading();
      showError(
        err?.message ||
          err?.error ||
          "Failed to download GRN Tech Form PDF",
      );
    }
  };

  const handleDownloadRequisitionPdf = async (
    salesOrderServiceId: string,
    salesOrderNumber: string,
  ) => {
    try {
      showLoading("Downloading PDF...");
      const res = await dispatch(
        downloadRequisitionPdf({
          salesOrderServiceId,
          salesOrderNumber,
        }),
      ).unwrap();

      hideLoading();
      if (res) {
        showSuccess("GRN Admin Form PDF downloaded successfully");
      }
    } catch (err: any) {
      hideLoading();
      showError(
        err?.message || err?.error || "Failed to download GRN Admin Form PDF",
      );
    }
  };

  const handleStatusChange = async (
    quotationId: string,
    newStatus: string,
    remark?: string
  ) => {
    try {
      await dispatch(
        handleServiceQuotationStatus({
          id: quotationId,
          quotation_status: newStatus,
          remark: remark,
        })
      ).unwrap();
      handleFetchQuotationData();
    } catch (err) {
      console.error("Error updating status:", err);
    }
  };

  // Columns definition
  const getColumns = () => {
    const commonColumns = [
      // {
      //   header: "Sr. No.",
      //   accessorKey: "sr_no",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: ({ row }: { row: any }) => row.original.sr_no || "-",
      // },
      {
        header: "Quotation No.",
        accessorKey: "quotation_number",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: API_ENDPOINT.QUOTATION.SERVICE.FILTER_DROPDOWN(
          "quotation_number",
          QUOTATION_STAGE.LOG
        ),
        cell: ({ row }: { row: any }) => row.original.quotation_number || "-",
      },
      // {
      //   header: "Service Number",
      //   accessorKey: "service_number",
      //   enableColumnFilter: true,
      //   enableSorting: true,
      //   filterType: "dropdown" as const,
      //   filterFetchUrl: API_ENDPOINT.QUOTATION.SERVICE.FILTER_DROPDOWN(
      //     "service_number",
      //     QUOTATION_STAGE.LOG
      //   ),
      //   cell: ({ row }: { row: any }) => row.original.service_number || "-",
      // },
      {
        header: "Quotation Date",
        accessorKey: "quotation_date",
        id: "quotation_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <FormattedDate date={row.original.quotation_date || "-"} />
        ),
      },
      {
        header: "Enquiry Ref",
        accessorKey: "enquiry_number",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: API_ENDPOINT.QUOTATION.SERVICE.FILTER_DROPDOWN(
          "enquiry_number",
          QUOTATION_STAGE.LOG
        ),
        cell: ({ row }: { row: any }) => row.original.enquiry_number || "-",
      },
      {
        header: "Customer Name",
        accessorKey: "customer_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: API_ENDPOINT.QUOTATION.SERVICE.FILTER_DROPDOWN(
          "customer_name",
          QUOTATION_STAGE.LOG
        ),
        cell: ({ row }: { row: any }) => row.original.customer_name || "-",
      },
      {
        header: "Organisation",
        id: "organisation_name",
        accessorFn: (row: any) => {
          const billFrom = row?.bill_from;
          if (!billFrom) return "-";
          let value = billFrom;
          if (typeof value === "string") {
            try {
              value = JSON.parse(value);
            } catch {
              return value.trim() || "-";
            }
          }
          if (typeof value !== "object" || Array.isArray(value)) return "-";
          return (
            value.organization_name ||
            value.organisation_name ||
            value.org_name ||
            value.name ||
            "-"
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ getValue }: { getValue: () => unknown }) =>
          (getValue() as string) || "-",
      },
      {
        header: "Agent",
        accessorKey: "agent_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: API_ENDPOINT.QUOTATION.SERVICE.FILTER_DROPDOWN(
          "agent_name",
          QUOTATION_STAGE.LOG
        ),
        cell: ({ row }: { row: any }) => row.original.agent_name || "-",
      },
     
      {
        header: "Grand Total",
        accessorKey: "grand_total_amount",
        id: "grand_total_amount",
        enableColumnFilter: false,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: API_ENDPOINT.QUOTATION.SERVICE.FILTER_DROPDOWN(
          "grand_total_amount",
          QUOTATION_STAGE.LOG
        ),
        cell: ({ row }: { row: any }) => {
          const value = row.original.grand_total_amount;
          const symbol = row.original.currency_symbol || "₹";
          return `${symbol} ${formatIndianAmount(value)}`;
        },
      },
      {
        header: "Status",
        accessorKey: "quotation_status",
        enableColumnFilter: false,
        enableSorting: false,
        filterType: "dropdown" as const,
        filterFetchUrl: API_ENDPOINT.QUOTATION.SERVICE.FILTER_DROPDOWN(
          "quotation_status",
          QUOTATION_STAGE.LOG
        ),
        cell: ({ row }: { row: any }) => {
          const status = row.original.quotation_status;
          return (
            <QuotationLogStatusDropdown
              currentStatus={status}
              itemId={row.original.quotation_service_id || row.original.id}
              onStatusChange={handleStatusChange}
              disabled={false}
              requireRemarks={true}
            />
          );
        },
      },
      {
        header: "Assigned To",
        accessorKey: "assigned_members",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const members = row.original?.assigned_members || [];

          if (!members.length) return "-";

          const names = members.map(
            (m: any) =>
              `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.username
          );

          const visible = names.slice(0, 4);
          const remaining = names.length - visible.length;

          return (
            <Tooltip
              arrow
              placement="top"
              title={names.join("\n")}
              componentsProps={{
                tooltip: {
                  sx: {
                    whiteSpace: "pre-line",
                    maxWidth: 250,
                    fontSize: "13px",
                  },
                },
              }}
            >
              <div style={{ cursor: "pointer", lineHeight: 1.4 }}>
                {visible.map((name: string, index: number) => (
                  <div key={index}>{name}</div>
                ))}

                {remaining > 0 && (
                  <div style={{ fontSize: "12px", color: "#6c757d" }}>
                    +{remaining} more
                  </div>
                )}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "Last Follow Up Date",
        accessorKey: "last_follow_up_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <FormattedDate
            date={row.original?.quotation_follow_ups?.[0]?.follow_up_date}
          />
        ),
      },
      {
        header: "Follow Up Remark",
        accessorKey: "follow_up_remark",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const rawHtml =
            row.original?.quotation_follow_ups?.[0]?.follow_up_remarks || "";

          if (!rawHtml) return <span>-</span>;

          const safeHtml = DOMPurify.sanitize(rawHtml);

          return (
            <Tooltip
              arrow
              placement="top"
              title={
                <div
                  style={{
                    maxWidth: "450px",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                  dangerouslySetInnerHTML={{ __html: safeHtml }}
                />
              }
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 500,
                    p: 1.5,
                  },
                },
              }}
            >
              <div
                style={{
                  minWidth: "200px",
                  maxWidth: "400px",
                  display: "-webkit-box",
                  WebkitLineClamp: 4,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  wordBreak: "break-word",
                  lineHeight: "1.5",
                  maxHeight: "6em", // 4 lines * 1.5 line-height
                  cursor: "pointer",
                }}
                dangerouslySetInnerHTML={{ __html: safeHtml }}
              />
            </Tooltip>
          );
        },
      },
      {
        header: "Next Follow Up Date",
        accessorKey: "next_follow_up_date",
        id: "next_follow_up_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <FormattedDate
            date={row.original?.quotation_follow_ups?.[0]?.next_follow_up_date}
          />
        ),
      },
      {
        header: "Created By/ Date",
        accessorKey: "created_at",
        id: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original.created_by_user?.username || "-"}
            date={row.original.created_at}
          />
        ),
      },
      {
        header: "Tags",
        accessorKey: "tags",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const tags: string[] = row.original.tags || [];
          if (!tags.length) return "-";

          const maxVisible = 3;
          const visible = tags.slice(0, maxVisible);
          const remaining = tags.length - visible.length;
          const tooltipText = tags.join(", ");

          return (
            <Tooltip
              arrow
              placement="top"
              title={tooltipText}
              componentsProps={{
                tooltip: {
                  sx: {
                    maxWidth: 300,
                    fontSize: "13px",
                    whiteSpace: "normal",
                  },
                },
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "4px",
                  minWidth: "150px",
                  cursor: "pointer",
                }}
              >
                {visible.map((tag: string) => (
                  <Chip key={tag}>{tag}</Chip>
                ))}
                {remaining > 0 && <Chip>+{remaining} more</Chip>}
              </div>
            </Tooltip>
          );
        },
      },
      {
        header: "Updated By/ Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <NameWithDate
            name={row.original.updated_by_user?.username || "-"}
            date={row.original.updated_at}
          />
        ),
      },
    ];

    const actionColumn = {
      header: "Action",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: { row: any }) => {
        const quotaionID = row.original.quotation_service_id;
        const data = row.original;

        const allActions = [
          {
            label: "View",
            permission: canView,
            icon: <VisibilityIcon fontSize="small" color="info" />,
            onClick: () => handleView(quotaionID),
          },
          {
            label: "Edit",
            permission: canUpdate,
            icon: <EditIcon fontSize="small" color="primary" />,
            onClick: () => handleEdit(quotaionID),
          },
          {
            label: "Follow up",
            permission: canUpdate,
            icon: <PersonAddIcon fontSize="small" color="success" />,
            onClick: () => {
              setSelectedFollowUpItem(data);
              setShowFollowUpModal(true);
            },
          },
          {
            label: "Download PDF",
            permission: canView,
            icon: <InsertDriveFileIcon fontSize="small" color="primary" />,
            onClick: () => {
              openPdfModal(quotaionID, data.quotation_number);
            },
          },
          ...(canView
            ? [
                {
                  label: "GRN Admin Form PDF",
                  permission: canView,
                  icon: (
                    <FileDownloadOutlinedIcon fontSize="small" color="primary" />
                  ),
                  onClick: () => {
                    handleDownloadRequisitionPdf(
                      data.sales_order_service_id || quotaionID,
                      data.sales_order_number || data.quotation_number || "",
                    );
                  },
                },
                {
                  label: "GRN Tech Form PDF",
                  permission: canView,
                  icon: (
                    <FileDownloadOutlinedIcon fontSize="small" color="primary" />
                  ),
                  onClick: () => {
                    handleDownloadCalibrationEngineerPdf(
                      data.sales_order_service_id || quotaionID,
                      data.sales_order_number || data.quotation_number || "",
                    );
                  },
                },
              ]
            : []),
          {
            label: data.enquiry_number ? "Revert" : "Delete",
            permission: canUpdate,
            icon: data.enquiry_number ? (
              <UndoIcon fontSize="small" color="warning" />
            ) : (
              <DeleteOutlineIcon fontSize="small" color="error" />
            ),
            onClick: () => handleDelete(quotaionID, data.enquiry_number),
            className: "text-danger",
          },
        ];

        // Filter actions based on quotation status
        const status = data.quotation_status;
        const deleteLabel = data.enquiry_number ? "Revert" : "Delete";
        let actions = allActions;

        if (status === "PO Received") {
          actions = allActions.filter(
            (action) =>
              action.label === "View" ||
              action.label === "Edit" ||
              action.label === "Download PDF" ||
              action.label === "GRN Admin Form PDF" ||
              action.label === "GRN Tech Form PDF" ||
              action.label === "Follow up"
          );
        } else if (status === "Lost") {
          actions = allActions.filter(
            (action) =>
              action.label === "View" ||
              action.label === deleteLabel ||
              action.label === "Download PDF" ||
              action.label === "GRN Admin Form PDF" ||
              action.label === "GRN Tech Form PDF" ||
              action.label === "Follow up"
          );
        }
        // For "Sent" and "Prepared", show all actions (default behavior)

        return <DynamicMenuItem row={row.original} menuItems={actions} />;
      },
    };
    return [...commonColumns, actionColumn];
  };

  return (
    <div className="quotation-listing-body">
      <div className="flex-shrink-0">
        <Row className="align-items-center quotation-search-row g-2">
          <SearchBox
            value={searchInput}
            onChange={(e: any) => setSearchInput(e.target.value)}
            placeholder="Search..."
          />

          <Col
            xs={12}
            md={12}
            lg={8}
            xl={7}
            className="d-flex justify-content-end align-items-center gap-2"
          >
            {tableInstance && (
              <ColumnVisibilityDropdown
                columns={tableInstance.getAllLeafColumns()}
                excludedColumns={["action"]}
              />
            )}
            {canView && (
              <ExportButton
                onExport={async () => {
                  return await exportServiceQuotationCSV({
                    search: search,
                    filters: filterValue,
                    type: QUOTATION_STAGE.LOG,
                  });
                }}
              />
            )}
          </Col>
        </Row>
      </div>

      <div
        className="quotation-table-scroll"
        style={{
          flex: "1 1 0",
          maxHeight: "100%",
          minHeight: 0,
          marginTop: 4,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <LoaderErrorWrapper loading={loading}>
          <MasterTableContainer
            columns={getColumns()}
            data={data}
            totalRecords={totalRecords}
            pageIndex={pageIndex}
            pageSize={pageSize}
            onPageChange={setPageIndex}
            onPageSizeChange={setPageSize}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            colValue={setFilterValue}
            sorting={sorting}
            onSortingChange={setSorting}
            manualSorting={true}
            isBordered={false}
            isPagination={true}
            isGlobalFilter={false}
            SearchPlaceholder="Search service quotations..."
            onTableReady={setTableInstance}
          />
        </LoaderErrorWrapper>
      </div>
      <FollowUpPopUp
        open={showFollowUpModal}
        onClose={() => {
          setShowFollowUpModal(false);
          handleFetchQuotationData(); // Refresh listing after closing follow-up modal
        }}
        title="Follow-up and Activities"
      >
        {selectedFollowUpItem && (
          <FollowupModel
            data={selectedFollowUpItem}
            activeType={QUOTATION_TYPE?.SERVICE}
            onClose={() => {
              setShowFollowUpModal(false);
              handleFetchQuotationData(); // Refresh listing after closing follow-up modal
            }}
          />
        )}
      </FollowUpPopUp>

      {/* Download PDF Modal (signature toggle) */}
      <Modal show={showPdfModal} onHide={closePdfModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Download PDF</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="service-pdf-with-signature-switch"
              label="Include Authorized Signature in PDF"
              checked={withSignature}
              onChange={(e) => setWithSignature(e.target.checked)}
              style={{ fontSize: "0.85rem", fontWeight: "500" }}
            />
          </Form.Group>
          <div
            className="mb-0 mt-2"
            style={{
              marginLeft: "calc(-1 * var(--bs-modal-padding, 1rem))",
              marginRight: "calc(-1 * var(--bs-modal-padding, 1rem))",
            }}
          >
            <hr className="mb-0 w-100" />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <FormActionButtons
            onCancel={closePdfModal}
            onSave={handleDownloadPdf}
            showSaveDraft={false}
            saveButtonLabel="Download PDF"
            isSaving={pdfDownloading}
            wrapperClassName="d-flex flex-wrap gap-2 justify-content-center w-100"
          />
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default QuotationLog;
